import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { NavLink, useLocation, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Icon } from "semantic-ui-react";
import { ThemeToggle } from "components";
import { Logo, ScreenSizes } from "components/shared";
import AccountHeader from "containers/header/AccountHeader";
import HelpAndFAQ from "components/helpAndFAQ";
import { setViewMode } from "actions/dashboard";
import { TURBINE_VIEW_MODES } from "../../../constants";

// Dashboard/Map/List aren't separate routes - they're view modes of the
// same "/" route (state.dashboard.viewMode), so they're nav buttons that
// dispatch setViewMode instead of plain route links.
const VIEW_MODE_NAV = [
  { label: "Dashboard", icon: "home", mode: TURBINE_VIEW_MODES.GRID },
  { label: "Map", icon: "map", mode: TURBINE_VIEW_MODES.MAP },
  { label: "List", icon: "list layout", mode: TURBINE_VIEW_MODES.LIST },
];

const SIDEBAR_WIDTH = "230px";

const Wrapper = styled.aside`
  display: flex;
  flex-direction: column;
  width: ${SIDEBAR_WIDTH};
  flex-shrink: 0;
  background-color: ${(props) => props.theme.sidebarBackground};
  color: ${(props) => props.theme.sidebarText};

  // Pinned to the viewport, not stretched to match Main's height - Shell
  // (PrivateRoute.js) is a flex row with no explicit height, so it grows
  // to fit whatever's tallest inside it. Without this, a long turbine
  // list in Main stretched this sidebar (default align-items: stretch)
  // to match, pushing Spacer/BottomSection (theme toggle, account/login,
  // help, logo) far below the fold - needing a long scroll to reach them
  // whenever there are enough turbines to make the list taller than one
  // screen. position: sticky (not fixed) keeps it participating in the
  // flex row's own layout/width instead of needing separate positioning.
  position: sticky;
  top: 0;
  align-self: flex-start;
  height: 100vh;
  overflow-y: auto;

  ${ScreenSizes.lessThan("mobile")`
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    height: auto;
    z-index: 1001;
    transform: translateX(${(props) => (props.open ? "0" : "-100%")});
    transition: transform 0.25s ease;
  `}
`;

const Backdrop = styled.div`
  display: none;

  ${ScreenSizes.lessThan("mobile")`
    display: ${(props) => (props.open ? "block" : "none")};
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 1000;
  `}
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
`;

const Wordmark = styled.span`
  font-weight: 800;
  font-size: 1.15rem;
  letter-spacing: 0.01em;
`;

const TriGreen = styled.span`
  color: #009246;
`;

const TriWhite = styled.span`
  color: #f4f5f0;
`;

const TriRed = styled.span`
  color: #ce2b37;
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  margin-top: 0.5rem;
`;

const navItemCss = `
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  text-decoration: none;
  font-weight: 600;
  font-family: inherit;
  font-size: 1rem;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    color: #fff;
  }
`;

const NavItem = styled((props) => <NavLink {...props} />)`
  ${navItemCss}
  color: ${(props) => props.theme.sidebarText};

  &.active {
    background-color: ${(props) => props.theme.sidebarActiveBackground};
    color: #fff;
  }
`;

const NavButton = styled.button`
  ${navItemCss}
  color: ${(props) => (props.active ? "#fff" : props.theme.sidebarText)};
  background-color: ${(props) => (props.active ? props.theme.sidebarActiveBackground : "transparent")};
`;

const Spacer = styled.div`
  flex: 1;
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const BrandFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  padding-top: 0.5rem;
`;

const Copyright = styled.span`
  font-size: 0.7rem;
  color: ${(props) => props.theme.sidebarText};
  opacity: 0.7;
`;

const Sidebar = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const viewMode = useSelector((state) => state.dashboard.viewMode);

  const handleViewModeClick = (mode) => {
    dispatch(setViewMode(mode));
    if (location.pathname !== "/") history.push("/");
    onClose();
  };

  return (
    <>
      <Backdrop open={open} onClick={onClose} />
      <Wrapper open={open}>
        <BrandRow>
          <Wordmark>
            <TriGreen>Ital</TriGreen>
            <TriWhite>SC</TriWhite>
            <TriRed>ADA</TriRed>
          </Wordmark>
        </BrandRow>
        <Nav>
          {VIEW_MODE_NAV.map((item) => (
            <NavButton
              key={item.label}
              type="button"
              active={location.pathname === "/" && viewMode === item.mode}
              onClick={() => handleViewModeClick(item.mode)}
            >
              <Icon name={item.icon} />
              {item.label}
            </NavButton>
          ))}
          <NavItem to="/licenses" onClick={onClose}>
            <Icon name="id badge outline" />
            Licenses
          </NavItem>
        </Nav>
        <Spacer />
        <BottomSection>
          <ThemeToggle light />
          <AccountHeader />
          <HelpAndFAQ />
          <BrandFooter>
            <Logo theme="dark" />
            <Copyright>© 2026 Orbital Italia</Copyright>
          </BrandFooter>
        </BottomSection>
      </Wrapper>
    </>
  );
};

Sidebar.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

Sidebar.defaultProps = {
  open: false,
  onClose: () => {},
};

export default Sidebar;
